import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Tabs,
  Card,
  Steps,
  Button,
  Space,
  Typography,
  Badge,
  message,
  Alert,
  Spin,
  Tag,
  Row,
  Col,
  Statistic,
} from "antd";
import {
  UploadOutlined,
  FileSearchOutlined,
  CheckCircleOutlined,
  BarChartOutlined,
  SafetyCertificateOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import {
  fetchPolicyDocuments,
  fetchChecklists,
  fetchPolicyTypes,
} from "../../../../Slices/policyCheckSlice";
import PolicyDocumentUpload from "./PolicyDocumentUpload";
import PolicyChecklistView from "./PolicyChecklistView";
import PolicyValidationView from "./PolicyValidationView";
import OneClickCompare from "./OneClickCompare";

const { Title, Text } = Typography;

const PolicyCheckTab = ({ requestId, RequestInfo }) => {
  const dispatch = useDispatch();
  const {
    documents,
    checklists,
    activeChecklist,
    comparisonSummary,
    validationResult,
    loading,
  } = useSelector((state) => state.policyCheck);

  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (requestId) {
      dispatch(fetchPolicyDocuments(requestId));
      dispatch(fetchChecklists(requestId));
      dispatch(fetchPolicyTypes());
    }
  }, [dispatch, requestId]);

  // Determine current step based on data state
  useEffect(() => {
    if (checklists.length > 0 && activeChecklist) {
      if (activeChecklist.status === "completed") {
        setActiveStep(3);
      } else if (comparisonSummary || activeChecklist.matchCount > 0) {
        setActiveStep(2);
      } else {
        setActiveStep(1);
      }
    } else if (documents.length > 0) {
      setActiveStep(1);
    }
  }, [documents, checklists, activeChecklist, comparisonSummary]);

  const completedDocs = documents.filter((d) => d.parsingStatus === "completed");
  const hasCompletedDoc = completedDocs.length > 0;
  const hasChecklist = checklists.length > 0;

  // Summary stats for the header
  const renderSummaryStats = () => {
    if (!activeChecklist) return null;

    const { totalItems, matchCount, notMatchCount, notFoundCount, pendingCount } = activeChecklist;

    return (
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={4}>
          <Card size="small">
            <Statistic title="Total Items" value={totalItems || 0} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Match"
              value={matchCount || 0}
              valueStyle={{ color: "#52c41a" }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Not Match"
              value={notMatchCount || 0}
              valueStyle={{ color: "#ff4d4f" }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Not Found"
              value={notFoundCount || 0}
              valueStyle={{ color: "#faad14" }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title="Pending" value={pendingCount || 0} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Score"
              value={totalItems > 0 ? Math.round((matchCount / totalItems) * 100) : 0}
              suffix="%"
              valueStyle={{
                color: matchCount / totalItems > 0.8 ? "#52c41a" : matchCount / totalItems > 0.5 ? "#faad14" : "#ff4d4f",
              }}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  const [activeTab, setActiveTab] = useState("ai-compare");

  const tabItems = [
    {
      key: "ai-compare",
      label: (
        <span style={{ fontWeight: 700 }}>
          <ThunderboltOutlined style={{ color: "#7c3aed" }} /> AI Compare
          <Tag
            style={{
              marginLeft: 6,
              background: "linear-gradient(135deg, #7c3aed, #2563eb)",
              color: "#fff",
              border: "none",
              fontSize: 9,
              lineHeight: "14px",
              padding: "0 4px",
            }}
          >
            ONE-CLICK
          </Tag>
        </span>
      ),
      children: (
        <OneClickCompare
          requestId={requestId}
          onComplete={() => {
            setActiveTab("checklist");
            dispatch(fetchChecklists(requestId));
            dispatch(fetchPolicyDocuments(requestId));
          }}
        />
      ),
    },
    {
      key: "upload",
      label: (
        <span>
          <UploadOutlined /> Documents
          {documents.length > 0 && (
            <Badge
              count={documents.length}
              size="small"
              style={{ marginLeft: 8 }}
            />
          )}
        </span>
      ),
      children: (
        <PolicyDocumentUpload
          requestId={requestId}
          RequestInfo={RequestInfo}
        />
      ),
    },
    {
      key: "checklist",
      label: (
        <span>
          <FileSearchOutlined /> Policy Checklist
          {hasChecklist && (
            <Tag
              color={activeChecklist?.status === "completed" ? "green" : "blue"}
              style={{ marginLeft: 8 }}
            >
              {activeChecklist?.status || checklists[0]?.status}
            </Tag>
          )}
        </span>
      ),
      children: (
        <PolicyChecklistView
          requestId={requestId}
          RequestInfo={RequestInfo}
        />
      ),
    },
    {
      key: "validation",
      label: (
        <span>
          <SafetyCertificateOutlined /> Validation & Insights
          {validationResult && (
            <Tag
              color={validationResult.criticalCount > 0 ? "red" : validationResult.warningCount > 0 ? "orange" : "green"}
              style={{ marginLeft: 8 }}
            >
              {validationResult.score}%
            </Tag>
          )}
        </span>
      ),
      children: (
        <PolicyValidationView
          requestId={requestId}
          RequestInfo={RequestInfo}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: "0 8px" }}>
      {/* Workflow Steps */}
      <Steps
        current={activeStep}
        size="small"
        style={{ marginBottom: 20 }}
        items={[
          {
            title: "Upload Old + New PDFs",
            description: hasCompletedDoc
              ? `${completedDocs.length} document(s)`
              : "Upload policy PDFs",
            icon: <UploadOutlined />,
          },
          {
            title: "AI Extracts & Compares",
            description: hasChecklist
              ? `${activeChecklist?.policyType || checklists[0]?.policyType} — Auto-filled`
              : "One-click AI analysis",
            icon: <ThunderboltOutlined />,
          },
          {
            title: "Review Discrepancies",
            description: activeChecklist?.notMatchCount > 0
              ? `${activeChecklist.notMatchCount} discrepancies`
              : "Review AI results",
            icon: <FileSearchOutlined />,
          },
          {
            title: "AM Review & Complete",
            description:
              activeChecklist?.status === "completed"
                ? "Completed"
                : "Account Manager sign-off",
            icon: <CheckCircleOutlined />,
          },
        ]}
      />

      {/* Summary Stats */}
      {renderSummaryStats()}

      {/* Main Content Tabs */}
      <Tabs items={tabItems} type="card" activeKey={activeTab} onChange={setActiveTab} />
    </div>
  );
};

export default PolicyCheckTab;
